import { useCallback, useEffect, useState } from 'react';
import { apiRequest } from '../../Services/api';
import { cloneCateringContent, DEFAULT_CATERING_CONTENT } from './cateringContent';
import './CateringPage.css';

const CANONICAL_URL = 'https://www.auntjackspeanutshack.com/catering/';

function setMeta(selector, attribute, value) {
  const element = document.head.querySelector(selector);
  if (element) element.setAttribute(attribute, value);
}

function formatPrice(value) {
  const number = Number(value);
  return number.toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(number) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

function CateringPage() {
  const [content, setContent] = useState(() => cloneCateringContent());
  const [contentNotice, setContentNotice] = useState('');

  const loadContent = useCallback(async () => {
    try {
      const data = await apiRequest('/catering/index.php');
      if (!data?.catering) throw new Error('The catering response did not include page content.');
      setContent(cloneCateringContent(data.catering));
      setContentNotice('');
    } catch {
      setContent(cloneCateringContent(DEFAULT_CATERING_CONTENT));
      setContentNotice('Current catering details could not be refreshed. Showing the standard package information.');
    }
  }, []);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  useEffect(() => {
    const priorTitle = document.title;
    const trackedElements = [
      ['meta[name="description"]', 'content'],
      ['meta[name="robots"]', 'content'],
      ['meta[property="og:title"]', 'content'],
      ['meta[property="og:description"]', 'content'],
      ['meta[property="og:url"]', 'content'],
      ['meta[name="twitter:title"]', 'content'],
      ['meta[name="twitter:description"]', 'content'],
      ['link[rel="canonical"]', 'href'],
    ];
    const priorValues = trackedElements.map(([selector, attribute]) => {
      const element = document.head.querySelector(selector);
      return { element, attribute, value: element?.getAttribute(attribute) };
    });

    document.title = content.seoTitle;
    setMeta('meta[name="description"]', 'content', content.seoDescription);
    setMeta('meta[name="robots"]', 'content', 'index,follow');
    setMeta('meta[property="og:title"]', 'content', content.seoTitle);
    setMeta('meta[property="og:description"]', 'content', content.seoDescription);
    setMeta('meta[property="og:url"]', 'content', CANONICAL_URL);
    setMeta('meta[name="twitter:title"]', 'content', content.seoTitle);
    setMeta('meta[name="twitter:description"]', 'content', content.seoDescription);
    setMeta('link[rel="canonical"]', 'href', CANONICAL_URL);

    const structuredData = document.createElement('script');
    structuredData.type = 'application/ld+json';
    structuredData.id = 'catering-structured-data';
    structuredData.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: "Aunt Jack's Boiled Peanut Catering",
      description: content.seoDescription,
      url: CANONICAL_URL,
      areaServed: {
        '@type': 'AdministrativeArea',
        name: content.serviceArea,
      },
      provider: {
        '@type': 'FoodEstablishment',
        name: "Aunt Jack's Peanut Shack",
        telephone: content.phone,
        email: content.email,
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Colbert',
          addressRegion: 'GA',
          addressCountry: 'US',
        },
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Boiled Peanut Bar Packages',
        itemListElement: content.packages.map((cateringPackage) => ({
          '@type': 'Offer',
          price: cateringPackage.pricePerPerson,
          priceCurrency: 'USD',
          eligibleQuantity: {
            '@type': 'QuantitativeValue',
            minValue: cateringPackage.minimumGuests,
            unitText: 'person',
          },
          itemOffered: {
            '@type': 'Service',
            name: cateringPackage.name,
            description: cateringPackage.description,
          },
        })),
      },
    });
    document.getElementById(structuredData.id)?.remove();
    document.head.appendChild(structuredData);

    return () => {
      document.title = priorTitle;
      priorValues.forEach(({ element, attribute, value }) => {
        if (!element) return;
        if (value === null || value === undefined) element.removeAttribute(attribute);
        else element.setAttribute(attribute, value);
      });
      structuredData.remove();
    };
  }, [content]);

  const highlightedIndex = content.heroHeading.toLocaleLowerCase().indexOf(content.heroHighlight.toLocaleLowerCase());
  const headingBefore = highlightedIndex >= 0 ? content.heroHeading.slice(0, highlightedIndex) : content.heroHeading;
  const headingAfter = highlightedIndex >= 0 ? content.heroHeading.slice(highlightedIndex + content.heroHighlight.length) : '';
  const phoneHref = content.phone.replace(/[^\d+]/g, '');
  return (
    <main className='catering-page'>
      {contentNotice ? <div className='catering-content-notice' role='status'>{contentNotice} <button type='button' onClick={loadContent}>Try again</button></div> : null}
      <section className='catering-hero' aria-labelledby='catering-heading'>
        <div className='catering-hero-copy'>
          <h1 id='catering-heading'>{headingBefore}{highlightedIndex >= 0 ? <span>{content.heroHighlight}</span> : null}{headingAfter}</h1>
          <p>{content.heroDescription}</p>
          <div className='catering-actions'>
            <a className='catering-button catering-button-primary' href={`tel:${phoneHref}`}>Call Jackie</a>
            <a className='catering-button catering-button-secondary' href={`mailto:${content.email}`}>Email for a quote</a>
          </div>
          <p className='catering-starting-price'>{content.heroStartingPrice}</p>
        </div>
        <div className='catering-hero-media'>
          <img
            src='/images/auntjack-pot-shack.jpg'
            alt='Jackie stirring a large pot beside the red Aunt Jack’s Peanut Shack trailer'
            width='813'
            height='520'
          />
        </div>
      </section>

      <section className='catering-packages' aria-labelledby='packages-heading'>
        <div className='catering-section-heading'>
          <h2 id='packages-heading'>{content.packagesHeading}</h2>
          <p>{content.packagesIntro}</p>
        </div>
        <div className='catering-package-grid'>
          {content.packages.map((cateringPackage) => (
            <article className='catering-package' key={cateringPackage.name}>
              <h3>{cateringPackage.name}</h3>
              <p className='catering-package-price'><span className='currency'>$</span>{formatPrice(cateringPackage.pricePerPerson)}<span>/person</span></p>
              <p className='catering-package-description'>{cateringPackage.description}</p>
              <p className='catering-package-minimum'>{cateringPackage.minimumGuests}-person minimum <span aria-hidden='true'>·</span> <strong>starts at ${formatPrice(cateringPackage.minimumTotal)}</strong></p>
            </article>
          ))}
        </div>
        <p className='catering-large-event'>{content.largeEventNote}</p>
      </section>

      <section className='catering-details' aria-labelledby='details-heading'>
        <div className='catering-details-inner'>
          <h2 id='details-heading'>{content.detailsHeading}</h2>
          <div className='catering-detail-columns'>
            <div className='catering-detail-group'>
              <h3>Travel</h3>
              <dl>
                {content.travel.map(({ label, price }) => (
                  <div key={`${label}-${price}`}><dt>{label}</dt><dd>{price}</dd></div>
                ))}
              </dl>
            </div>
            <div className='catering-detail-group'>
              <h3>Add-ons</h3>
              <dl>
                {content.addOns.map(({ label, price }) => (
                  <div key={`${label}-${price}`}><dt>{label}</dt><dd>{price}</dd></div>
                ))}
              </dl>
            </div>
          </div>
          <p className='catering-market-note'>{content.marketNote}</p>
        </div>
      </section>

      <section className='catering-contact' aria-labelledby='catering-contact-heading'>
        <div className='catering-contact-media'>
          <img
            src='/images/auntjack-pot-shack.jpg'
            alt='Aunt Jack’s Peanut Shack preparing a fresh outdoor boil in Colbert, Georgia'
            loading='lazy'
            width='813'
            height='520'
          />
        </div>
        <div className='catering-contact-copy'>
          <h2 id='catering-contact-heading'>{content.contactHeading}</h2>
          <p>{content.contactDescription}</p>
          <div className='catering-actions catering-contact-actions'>
            <a className='catering-button catering-button-gold' href={`tel:${phoneHref}`}>Call {content.phone}</a>
            <a className='catering-button catering-button-light' href={`mailto:${content.email}`}>Email {content.email}</a>
          </div>
          <p className='catering-service-area'>{content.serviceArea}</p>
        </div>
      </section>
    </main>
  );
}

export default CateringPage;
